/**
 * Listing enums + labels mirrored from the backend.
 *
 * Keeping them here avoids a `/api/lookups/listing` round trip on every page
 * load — these lists change rarely, and the backend validates on write anyway.
 */

export const PLACE_TYPES = [
  'private_room', 'shared_room', 'basement_apartment', 'studio',
  'one_bedroom', 'two_bedroom', 'three_plus_bedroom', 'whole_house',
];

export const PLACE_TYPE_LABELS = {
  private_room: 'Private room',
  shared_room: 'Shared room',
  basement_apartment: 'Basement apartment',
  studio: 'Studio',
  one_bedroom: '1 bedroom',
  two_bedroom: '2 bedroom',
  three_plus_bedroom: '3+ bedroom',
  whole_house: 'Whole house',
};

export const FURNISHED = [
  { value: 'fully', label: 'Fully furnished' },
  { value: 'partial', label: 'Partially furnished' },
  { value: 'unfurnished', label: 'Unfurnished' },
];

export const BATHROOM_TYPES = [
  { value: 'private', label: 'Private' },
  { value: 'shared', label: 'Shared' },
];

export const PARKING_TYPES = [
  { value: 'driveway', label: 'Driveway' },
  { value: 'street', label: 'Street' },
  { value: 'garage', label: 'Garage' },
  { value: 'none', label: 'No parking' },
];

export const PETS = [
  { value: 'no', label: 'No pets' },
  { value: 'cats', label: 'Cats only' },
  { value: 'dogs', label: 'Dogs only' },
  { value: 'both', label: 'Cats and dogs' },
  { value: 'case_by_case', label: 'Case by case' },
];

export const SMOKING = [
  { value: 'no', label: 'No smoking' },
  { value: 'outdoor', label: 'Outdoor only' },
  { value: 'yes', label: 'Smoking allowed' },
];

export const GUESTS = [
  { value: 'no_overnight', label: 'No overnight guests' },
  { value: 'occasional', label: 'Occasional overnight guests' },
  { value: 'welcome', label: 'Guests welcome' },
];

export const LEASE_TYPES = [
  { value: 'fixed_term', label: 'Fixed-term' },
  { value: 'month_to_month', label: 'Month-to-month' },
];

export const UTILITIES = [
  { value: 'water', label: 'Water' },
  { value: 'hydro', label: 'Hydro (electricity)' },
  { value: 'heat', label: 'Heat' },
  { value: 'gas', label: 'Gas' },
  { value: 'trash', label: 'Trash' },
];

export const AMENITY_CATEGORIES = {
  Essentials: [
    'wifi', 'kitchen', 'washer', 'dryer', 'heating', 'ac', 'hot_water',
    'dishwasher', 'microwave', 'oven', 'fridge',
  ],
  Bedroom: [
    'bed_linens', 'bed_sizes', 'desk', 'closet', 'blackout_curtains',
    'room_lock', 'ceiling_fan',
  ],
  Bathroom: [
    'private_bath', 'shared_bath', 'bathtub', 'shower', 'hair_dryer', 'towels',
  ],
  Kitchen: [
    'kitchen_private', 'kitchen_shared', 'cookware', 'dishes',
    'coffee_maker', 'toaster', 'kettle',
  ],
  Building: [
    'elevator', 'gym', 'pool', 'security_camera', 'smoke_detector',
    'co_detector', 'fire_extinguisher', 'first_aid', 'smart_lock',
  ],
  'Parking & Transport': [
    'driveway', 'street_parking', 'garage_parking', 'transit_5min',
    'transit_10min', 'bike_storage',
  ],
  Outdoor: ['backyard', 'balcony', 'patio', 'garden'],
  Lifestyle: [
    'vegetarian_friendly', 'halal_nearby', 'near_gurdwara', 'near_mosque',
    'near_mandir', 'near_church', 'multilingual_landlord',
    'international_student_friendly',
  ],
};

export const AMENITY_LABELS = {
  wifi: 'Wi-Fi', kitchen: 'Kitchen', washer: 'Washer', dryer: 'Dryer',
  heating: 'Heating', ac: 'Air conditioning', hot_water: 'Hot water',
  dishwasher: 'Dishwasher', microwave: 'Microwave', oven: 'Oven', fridge: 'Fridge',
  bed_linens: 'Bed linens', bed_sizes: 'Bed provided', desk: 'Desk',
  closet: 'Closet', blackout_curtains: 'Blackout curtains',
  room_lock: 'Lockable bedroom', ceiling_fan: 'Ceiling fan',
  private_bath: 'Private bathroom', shared_bath: 'Shared bathroom',
  bathtub: 'Bathtub', shower: 'Shower', hair_dryer: 'Hair dryer', towels: 'Towels',
  kitchen_private: 'Private kitchen', kitchen_shared: 'Shared kitchen',
  cookware: 'Cookware', dishes: 'Dishes & cutlery',
  coffee_maker: 'Coffee maker', toaster: 'Toaster', kettle: 'Kettle',
  elevator: 'Elevator', gym: 'Gym', pool: 'Pool',
  security_camera: 'Security camera', smoke_detector: 'Smoke detector',
  co_detector: 'CO detector', fire_extinguisher: 'Fire extinguisher',
  first_aid: 'First-aid kit', smart_lock: 'Smart lock',
  driveway: 'Driveway parking', street_parking: 'Street parking',
  garage_parking: 'Garage parking',
  transit_5min: 'Transit < 5 min walk', transit_10min: 'Transit < 10 min walk',
  bike_storage: 'Bike storage',
  backyard: 'Backyard', balcony: 'Balcony', patio: 'Patio', garden: 'Garden',
  vegetarian_friendly: 'Vegetarian-friendly household',
  halal_nearby: 'Halal groceries nearby',
  near_gurdwara: 'Near a Gurdwara', near_mosque: 'Near a Mosque',
  near_mandir: 'Near a Mandir', near_church: 'Near a Church',
  multilingual_landlord: 'Multilingual landlord',
  international_student_friendly: 'International-student friendly',
};

export const PHOTO_ROOM_LABELS = [
  'Exterior', 'Living room', 'Kitchen', 'Bedroom', 'Bathroom',
  'Dining', 'Hallway', 'Laundry', 'Parking', 'Backyard', 'Other',
];

export const GTA_CITIES = [
  'Toronto', 'Mississauga', 'Brampton', 'Markham', 'Vaughan',
  'Richmond Hill', 'Oakville', 'Burlington', 'Ajax', 'Pickering',
  'Whitby', 'Oshawa', 'Milton', 'Newmarket', 'Aurora',
];

export const TORONTO_NEIGHBORHOODS = [
  'Scarborough', 'North York', 'Etobicoke', 'East York', 'York',
];

export const WIZARD_STEPS = [
  { key: 'start', label: 'Get started' },
  { key: 'property-type', label: 'Property type' },
  { key: 'location', label: 'Location' },
  { key: 'photos', label: 'Photos' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'lease', label: 'Lease terms' },
  { key: 'amenities', label: 'Amenities' },
  { key: 'rules', label: 'House rules' },
  { key: 'description', label: 'Description' },
  { key: 'review', label: 'Review & submit' },
];

/** Compute all-in monthly cost from a draft — mirror of backend formula. */
export function computeAllIn(d) {
  const rent = Number(d.monthly_rent_cad || 0);
  const util = d.includes_utilities ? 0 : Number(d.additional_utility_cost_cad || 0);
  const net = d.includes_internet ? 0 : Number(d.internet_cost_if_separate_cad || 0);
  const park = d.includes_parking ? 0 : Number(d.parking_cost_if_separate_cad || 0);
  const fees = (d.other_fees || []).reduce((s, f) => s + Number(f.amount_cad || 0), 0);
  return rent + util + net + park + fees;
}
